/**
 * JS wrapper for the native voice interaction surface.
 *
 * Keep STT/TTS plugin imports and Android AudioFocus bridging contained here.
 * UI code talks to this module through a small, testable contract.
 */

import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { QueueStrategy, TextToSpeech } from '@capacitor-community/text-to-speech';
import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isPluginAvailable, platform } from './platform';

export const NOT_IMPLEMENTED_ERROR = 'voice-native-not-implemented-in-v1';

export type VoicePermissionState = 'granted' | 'denied' | 'prompt';
export type AudioFocusEventType = 'audioFocusLost' | 'audioFocusDuckOther';
export type VoiceNativeErrorCode =
    | 'voice_unsupported'
    | 'stt_unavailable'
    | 'stt_timeout'
    | 'stt_no_speech'
    | 'stt_error'
    | 'tts_empty'
    | 'tts_error'
    | 'audio_focus_denied'
    | 'audio_focus_unavailable'
    | 'audio_focus_error';

interface VoiceAudioFocusPlugin {
    hasMicPermission(): Promise<{ granted: boolean }>;
    openAppSettings(): Promise<void>;
    requestFocus(): Promise<void>;
    abandonFocus(): Promise<void>;
    addListener(
        eventName: 'focusChange',
        listener: (event: { type?: unknown }) => void,
    ): Promise<PluginListenerHandle>;
}

const VoiceAudioFocus = registerPlugin<VoiceAudioFocusPlugin>('VoiceAudioFocus');

export function canUseVoice(): boolean {
    return platform() === 'android'
        && Capacitor.isNativePlatform()
        && isPluginAvailable('VoiceAudioFocus');
}

export async function getPermissionState(): Promise<VoicePermissionState> {
    if (!canUseVoice()) return 'denied';
    try {
        const result = await VoiceAudioFocus.hasMicPermission();
        return normalizePermissionState(result);
    } catch {
        return 'denied';
    }
}

/**
 * Request RECORD_AUDIO at runtime. Capacitor WebView surfaces
 * getUserMedia({ audio: true }) as the Android runtime permission dialog. The
 * stream is stopped immediately because PR-V1 only asks permission.
 */
export async function requestPermission(): Promise<VoicePermissionState> {
    if (!canUseVoice()) return 'denied';
    if (!navigator.mediaDevices?.getUserMedia) return 'denied';
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        return 'granted';
    } catch {
        return 'denied';
    }
}

export async function openAppSettings(): Promise<void> {
    if (!canUseVoice()) return;
    await VoiceAudioFocus.openAppSettings();
}

export function normalizePermissionState(value: unknown): VoicePermissionState {
    if (!value || typeof value !== 'object') return 'prompt';
    const granted = (value as { granted?: unknown }).granted;
    if (granted === true) return 'granted';
    if (granted === false) return 'denied';
    return 'prompt';
}

export interface StartListeningOptions {
    timeoutMs?: number;
}

export async function startListening(options: StartListeningOptions = {}): Promise<string> {
    assertVoiceSupported();
    const available = await SpeechRecognition.available()
        .catch(() => ({ available: false }));
    if (!available.available) throw voiceError('stt_unavailable');

    const timeoutMs = options.timeoutMs ?? 5000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            void SpeechRecognition.stop().catch(() => {});
            reject(voiceError('stt_timeout'));
        }, timeoutMs);
    });

    try {
        const result = await Promise.race([
            SpeechRecognition.start({
                popup: false,
                partialResults: false,
                maxResults: 1,
            }),
            timeout,
        ]);
        const transcript = result.matches?.[0]?.trim();
        if (!transcript) throw voiceError('stt_no_speech');
        return transcript;
    } catch (err) {
        if (isVoiceNativeError(err, 'stt_unavailable', 'stt_timeout', 'stt_no_speech')) {
            throw err;
        }
        throw voiceError('stt_error');
    } finally {
        if (timeoutId !== null) clearTimeout(timeoutId);
    }
}

export async function stopListening(): Promise<void> {
    if (!canUseVoice()) return;
    await SpeechRecognition.stop().catch(() => {});
}

export async function speak(text: string): Promise<void> {
    assertVoiceSupported();
    const trimmed = text.trim();
    if (!trimmed) throw voiceError('tts_empty');
    try {
        await TextToSpeech.speak({
            text: trimmed,
            lang: 'en-US',
            rate: 1.0,
            pitch: 1.0,
            volume: 1.0,
            queueStrategy: QueueStrategy.Flush,
        });
    } catch (err) {
        if (isVoiceNativeError(err, 'tts_empty')) throw err;
        throw voiceError('tts_error');
    }
}

export async function stopSpeaking(): Promise<void> {
    if (!canUseVoice()) return;
    await TextToSpeech.stop().catch(() => {});
}

export async function acquireAudioFocus(): Promise<void> {
    assertVoiceSupported();
    try {
        await VoiceAudioFocus.requestFocus();
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('audio_focus_denied')) throw voiceError('audio_focus_denied');
        if (message.includes('audio_focus_unavailable')) throw voiceError('audio_focus_unavailable');
        throw voiceError('audio_focus_error');
    }
}

export async function releaseAudioFocus(): Promise<void> {
    if (!canUseVoice()) return;
    await VoiceAudioFocus.abandonFocus().catch(() => {});
}

export async function addAudioFocusListener(
    listener: (event: AudioFocusEventType) => void,
): Promise<{ remove(): void | Promise<void> }> {
    if (!canUseVoice()) return { remove: () => {} };
    return VoiceAudioFocus.addListener('focusChange', (event) => {
        const normalized = normalizeAudioFocusEvent(event);
        if (normalized) listener(normalized);
    });
}

export function normalizeAudioFocusEvent(value: unknown): AudioFocusEventType | null {
    if (!value || typeof value !== 'object') return null;
    const type = (value as { type?: unknown }).type;
    if (type === 'audioFocusLost' || type === 'audioFocusDuckOther') return type;
    return null;
}

export function voiceError(code: VoiceNativeErrorCode): Error & { code: VoiceNativeErrorCode } {
    const err = new Error(code) as Error & { code: VoiceNativeErrorCode };
    err.code = code;
    return err;
}

export function isVoiceNativeError(err: unknown, ...codes: VoiceNativeErrorCode[]): boolean {
    const code = typeof err === 'object' && err !== null
        ? (err as { code?: unknown }).code
        : null;
    const message = err instanceof Error ? err.message : '';
    return codes.some((candidate) => code === candidate || message === candidate);
}

function assertVoiceSupported(): void {
    if (!canUseVoice()) throw voiceError('voice_unsupported');
}
